import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, Stack, Group, Title, Text, Switch, Tooltip, Loader, Center, Divider } from "@mantine/core";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { commsApi, type CommsChannel, type CommsPreference } from "@/lib/api/comms";
import { TargetSection } from "@/components/notification-targets/TargetSection";
import { tenantApi } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth";
import { subscribeToPush, unsubscribeFromPush } from "@/lib/push";
import { BrowserNotificationsCard } from "@/components/notifications/browser-notifications-card";
import { needsHomeScreen, readPwaEnv } from "@/lib/pwa";

/** Channel keys that ride the browser Push API and therefore need the
 *  service-worker subscribe/unsubscribe dance, not just a preference write. */
function isWebPush(key: string): boolean {
  return key === "web_push" || key === "push";
}

/** Why an unavailable channel is greyed out — surfaced in a tooltip so the
 *  member knows what to do to unlock it. */
function channelHint(key: string): string {
  switch (key) {
    case "sms":
      return "Add a phone number to your account to use SMS.";
    case "slack":
      return "Link Slack to receive notifications there.";
    case "web_push":
    case "push":
      return needsHomeScreen(readPwaEnv())
        ? "Add the portal to your Home Screen first, then turn on notifications there."
        : "Turn on notifications for this browser above.";
    case "portal":
      return "You are rung in the portal while it is open in a tab or as an installed app.";
    default:
      return "This channel isn't available yet.";
  }
}

export default function NotificationSettingsPage() {
  const queryClient = useQueryClient();
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  // The persisted auth store predates ``is_site_admin`` for anyone already
  // signed in, and it is only refreshed on login / tenant change — so read the
  // admin flags from a fresh ``/me/`` and fall back to the store meanwhile.
  const storedUser = useAuthStore((s) => s.user);
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => tenantApi.getMe().then((r) => r.data),
    staleTime: 60_000,
  });
  const user = me ?? storedUser;

  const {
    data: preferences = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["comms-preferences"],
    queryFn: () => commsApi.getPreferences().then((r) => r.data),
  });

  const mutation = useMutation({
    mutationFn: ({ category, channels }: { category: string; channels: string[] }) =>
      commsApi.updatePreference(category, channels),
    onSuccess: () => {
      toast.success("Notification preferences saved");
      queryClient.invalidateQueries({ queryKey: ["comms-preferences"] });
    },
    onError: () => toast.error("Failed to save notification preferences"),
    onSettled: () => setPendingKey(null),
  });

  const handleToggle = async (pref: CommsPreference, channel: CommsChannel, next: boolean) => {
    const key = `${pref.category}:${channel.key}`;
    setPendingKey(key);
    try {
      // Web push needs the browser subscription in place before (or removed
      // after) we record the preference — otherwise the backend would have an
      // opt-in with nowhere to deliver.
      if (isWebPush(channel.key)) {
        if (next) {
          await subscribeToPush();
        } else {
          await unsubscribeFromPush();
        }
      }
      // The endpoint replaces the whole row, so send the full desired set.
      const channels = pref.channels
        .filter((c) => c.enabled && c.key !== channel.key)
        .map((c) => c.key);
      if (next) channels.push(channel.key);
      mutation.mutate({ category: pref.category, channels });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't update this channel");
      setPendingKey(null);
    }
  };

  return (
    <Stack p="md" gap="lg">
      <Group gap="sm">
        <Bell size={22} style={{ color: "var(--mantine-primary-color-filled)" }} />
        <Title order={2}>Notifications</Title>
      </Group>
      <Stack gap={4}>
        <Title order={4}>My notifications</Title>
        <Text fz="sm" c="dimmed" maw={640}>
          Choose how you want to be notified for each kind of update. This is about you —
          your inbox, your phone, this browser. Unavailable channels need a bit of setup
          first — hover them to see what's missing.
        </Text>
      </Stack>

      <BrowserNotificationsCard />

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : isError ? (
        <Text fz="sm" c="red">
          Couldn't load your notification preferences. Please try again later.
        </Text>
      ) : preferences.length === 0 ? (
        <Text fz="sm" c="dimmed">
          There are no notification categories available for your account yet.
        </Text>
      ) : (
        <Stack gap="md">
          {preferences.map((pref) => (
            <Card key={pref.category} withBorder padding="lg">
              <Stack gap="xs" mb="md">
                <Text fw={600}>{pref.label}</Text>
                {pref.description && (
                  <Text fz="xs" c="dimmed">
                    {pref.description}
                  </Text>
                )}
              </Stack>
              <Stack gap="sm">
                {pref.channels.map((channel) => {
                  const key = `${pref.category}:${channel.key}`;
                  const busy = pendingKey === key;
                  const control = (
                    <Switch
                      checked={channel.enabled}
                      disabled={!channel.available || busy || mutation.isPending}
                      onChange={(e) => handleToggle(pref, channel, e.currentTarget.checked)}
                      aria-label={`${channel.label} for ${pref.label}`}
                    />
                  );
                  return (
                    <Group key={channel.key} justify="space-between" wrap="nowrap">
                      <Text fz="sm" c={channel.available ? undefined : "dimmed"}>
                        {channel.label}
                      </Text>
                      {channel.available ? (
                        control
                      ) : (
                        <Tooltip label={channelHint(channel.key)} withArrow>
                          {/* Tooltip needs a hoverable wrapper — a disabled
                              Switch swallows pointer events on its own. */}
                          <Group gap={0}>{control}</Group>
                        </Tooltip>
                      )}
                    </Group>
                  );
                })}
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {(user?.is_tenant_admin || user?.is_site_admin) && (
        <>
          <Divider />
          <Stack gap={4}>
            <Title order={4}>Shared destinations</Title>
            <Text fz="sm" c="dimmed" maw={640}>
              These are not your personal settings. A destination is a fixed address —
              a shared mailbox, a phone number, a Slack channel — that gets notified on
              behalf of everyone, whether or not anyone is signed in. You manage them
              because you're an admin.
            </Text>
          </Stack>
        </>
      )}

      {user?.is_tenant_admin && <TargetSection scope="tenant" />}
      {user?.is_site_admin && <TargetSection scope="site" />}
    </Stack>
  );
}
